/**
 * Etecplus lists: wishlist, product compare and recently viewed, stored in the browser (no app).
 * Loaded with `defer` from layout/theme.liquid. No dependencies.
 *
 * Storage: three localStorage keys, each a JSON array of product handles.
 * Product cards and data come from the `etec-product-data` section (Section Rendering API).
 */
(() => {
  const KEYS = { wishlist: 'etec:wishlist', compare: 'etec:compare', recent: 'etec:recent' };
  const LIMITS = { wishlist: 100, compare: 4, recent: 12 };
  const CHANGE_EVENT = 'etec:lists:change';
  const DATA_SECTION = 'etec-product-data';

  const TEXT = {
    wishlistAdded: (title) => `${title} added to wishlist`,
    wishlistRemoved: (title) => `${title} removed from wishlist`,
    compareAdded: (title) => `${title} added to compare`,
    compareRemoved: (title) => `${title} removed from compare`,
    compareFull: `You can compare up to ${LIMITS.compare} products. Remove one to add another.`,
    compareCleared: 'Compare list cleared',
    wishlistCleared: 'Wishlist cleared',
    compareCount: (count) => `${count} of ${LIMITS.compare} to compare`,
    compareLink: 'Compare',
    compareClear: 'Clear',
    compareRegion: 'Compare products',
    saved: (count) => `${count} saved ${count === 1 ? 'product' : 'products'}`,
    items: (count) => `${count} ${count === 1 ? 'item' : 'items'}`,
    from: 'From',
    price: 'Price',
    availability: 'Stock',
    inStock: 'In stock',
    outOfStock: 'Out of stock',
    general: 'General',
    product: 'Product',
    remove: (title) => `Remove ${title} from compare`,
    missing: '—',
    notProvided: 'Not provided',
  };

  /* ---------- Storage ------------------------------------------------------- */

  const memory = {};

  const clean = (value, list) => {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    const handles = [];
    value.forEach((handle) => {
      if (typeof handle !== 'string' || !handle || handle.length > 255 || seen.has(handle)) return;
      seen.add(handle);
      handles.push(handle);
    });
    return handles.slice(0, LIMITS[list]);
  };

  const store = {
    read(list) {
      try {
        const raw = window.localStorage.getItem(KEYS[list]);
        const handles = clean(raw ? JSON.parse(raw) : [], list);
        memory[list] = handles;
        return handles.slice();
      } catch (error) {
        return (memory[list] || []).slice();
      }
    },

    write(list, handles) {
      const next = clean(handles, list);
      memory[list] = next;
      try {
        window.localStorage.setItem(KEYS[list], JSON.stringify(next));
      } catch (error) {
        /* Storage is unavailable (private mode, blocked cookies): keep the list for this page view. */
      }
      emit(list);
    },

    has(list, handle) {
      return store.read(list).includes(handle);
    },

    /** Returns 'added', 'exists' or 'full'. */
    add(list, handle) {
      if (!handle || !KEYS[list]) return 'exists';
      const handles = store.read(list);

      if (list === 'recent') {
        if (handles[0] === handle) return 'exists';
        store.write(list, [handle].concat(handles.filter((item) => item !== handle)));
        return 'added';
      }

      if (handles.includes(handle)) return 'exists';
      if (handles.length >= LIMITS[list]) return 'full';
      store.write(list, list === 'wishlist' ? [handle].concat(handles) : handles.concat(handle));
      return 'added';
    },

    remove(list, handle) {
      const handles = store.read(list);
      if (!handles.includes(handle)) return false;
      store.write(
        list,
        handles.filter((item) => item !== handle)
      );
      return true;
    },

    clear(list) {
      store.write(list, []);
    },
  };

  function emit(list) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list, handles: store.read(list) } }));
  }

  // Another tab changed a list.
  window.addEventListener('storage', (event) => {
    if (event.key === null) {
      Object.keys(KEYS).forEach(emit);
      return;
    }
    const list = Object.keys(KEYS).find((name) => KEYS[name] === event.key);
    if (list) emit(list);
  });

  /* ---------- Live region ----------------------------------------------------- */

  let liveRegion;
  let liveTimer;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.dataset.etecListsLive = '';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.clearTimeout(liveTimer);
    liveTimer = window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 60);
  }

  /* ---------- URLs and product data ------------------------------------------- */

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  // The page URLs are defined once, in snippets/etec-list-url.liquid, and reach this script on the header link.
  const pageUrl = (list) => {
    const source = document.querySelector(`[data-etec-${list}-url]`);
    return (source && source.getAttribute(`data-etec-${list}-url`)) || `${rootUrl()}pages/${list}`;
  };

  const productCache = new Map();

  /**
   * Resolves to { data, card, buy } for a product, `null` when the product no longer exists (404),
   * or `undefined` when the request failed for another reason (kept in the list, retried next time).
   */
  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    const url = `${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${DATA_SECTION}`;
    const request = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`etec-lists: ${response.status}`);
        return response.text().then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const json = doc.querySelector('script[data-etec-product]');
          const card = doc.querySelector('template[data-etec-card]');
          if (!json || !card) return null;
          return {
            data: JSON.parse(json.textContent),
            card,
            buy: doc.querySelector('template[data-etec-buy]'),
          };
        });
      })
      .catch(() => {
        productCache.delete(handle);
        return undefined;
      });

    productCache.set(handle, request);
    return request;
  }

  const cardFor = (handle, product) => {
    const item = document.importNode(product.card.content, true).querySelector('li');
    if (item) item.dataset.etecHandle = handle;
    return item;
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  /* ---------- Toggle button ----------------------------------------------------- */

  class EtecListToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.onClick = this.onClick || this.toggle.bind(this);
      this.onChange =
        this.onChange ||
        ((event) => {
          if (event.detail.list === this.dataset.list) this.sync(event.detail.handles);
        });

      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    sync(handles) {
      const active = (handles || store.read(this.dataset.list)).includes(this.dataset.handle);
      this.button.setAttribute('aria-pressed', String(active));
      this.classList.toggle('is-active', active);
    }

    toggle(event) {
      event.preventDefault();
      event.stopPropagation();

      const { list, handle } = this.dataset;
      const title = this.dataset.title || TEXT.product;

      if (store.has(list, handle)) {
        store.remove(list, handle);
        announce(list === 'compare' ? TEXT.compareRemoved(title) : TEXT.wishlistRemoved(title));
        return;
      }

      const result = store.add(list, handle);
      if (result === 'full') {
        compareBar.notify(TEXT.compareFull);
        announce(TEXT.compareFull);
        return;
      }
      announce(list === 'compare' ? TEXT.compareAdded(title) : TEXT.wishlistAdded(title));
    }
  }

  /* ---------- Count badge --------------------------------------------------------- */

  class EtecListCount extends HTMLElement {
    connectedCallback() {
      this.onChange =
        this.onChange ||
        ((event) => {
          if (event.detail.list === this.dataset.list) this.sync(event.detail.handles.length);
        });
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.sync(store.read(this.dataset.list).length);
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    sync(count) {
      this.textContent = '';
      this.hidden = count === 0;
      if (count === 0) return;
      const visible = el('span', '', count > 99 ? '99+' : String(count));
      visible.setAttribute('aria-hidden', 'true');
      this.append(visible, el('span', 'visually-hidden', TEXT.items(count)));
    }
  }

  /* ---------- Compare bar ------------------------------------------------------------ */

  const compareBar = {
    node: null,
    timer: null,

    build() {
      const bar = el('div', 'etec-compare-bar');
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', TEXT.compareRegion);
      bar.hidden = true;

      const inner = el('div', 'etec-compare-bar__inner page-width');
      this.count = el('p', 'etec-compare-bar__count');
      this.message = el('p', 'etec-compare-bar__message');
      this.message.hidden = true;

      const actions = el('div', 'etec-compare-bar__actions');
      this.link = el('a', 'etec-button etec-button--small etec-button--accent etec-compare-bar__link', TEXT.compareLink);
      this.link.href = pageUrl('compare');
      const clear = el('button', 'etec-compare-bar__clear', TEXT.compareClear);
      clear.type = 'button';
      clear.addEventListener('click', () => {
        store.clear('compare');
        announce(TEXT.compareCleared);
      });
      actions.append(this.link, clear);

      inner.append(this.count, this.message, actions);
      bar.append(inner);
      document.body.appendChild(bar);
      this.node = bar;
    },

    sync(handles) {
      // The compare page already shows the list, so the bar stays away there.
      const count = document.querySelector('etec-compare') ? 0 : (handles || store.read('compare')).length;
      if (!this.node && count === 0) return;
      if (!this.node) this.build();

      this.node.hidden = count === 0;
      document.documentElement.classList.toggle('etec-has-compare-bar', count > 0);
      this.count.textContent = TEXT.compareCount(count);
      if (count < LIMITS.compare) this.hideMessage();
    },

    notify(message) {
      this.sync();
      if (!this.node || this.node.hidden) return;
      this.message.textContent = message;
      this.message.hidden = false;
      this.node.classList.add('is-warning');
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.hideMessage(), 5000);
    },

    hideMessage() {
      if (!this.node) return;
      window.clearTimeout(this.timer);
      this.message.hidden = true;
      this.message.textContent = '';
      this.node.classList.remove('is-warning');
    },
  };

  document.addEventListener(CHANGE_EVENT, (event) => {
    if (event.detail.list === 'compare') compareBar.sync(event.detail.handles);
  });

  /* ---------- Recently viewed: record ---------------------------------------------- */

  class EtecRecentTracker extends HTMLElement {
    connectedCallback() {
      // The product page markup is reused inside the quick add dialog: that is not a product view.
      if (this.closest('quick-add-modal') || !this.dataset.handle) return;
      store.add('recent', this.dataset.handle);
    }
  }

  /* ---------- Recently viewed: show ------------------------------------------------- */

  class EtecRecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-etec-grid]');
      this.wrapper = this.closest('.shopify-section');
      if (!this.grid) return;
      this.render();
    }

    async render() {
      const limit = parseInt(this.dataset.limit, 10) || 5;
      const current = this.dataset.current;
      // Ask for a couple more than needed so deleted products don't leave gaps.
      const handles = store
        .read('recent')
        .filter((handle) => handle !== current)
        .slice(0, limit + 2);

      const products = handles.length ? await Promise.all(handles.map(fetchProduct)) : [];
      if (!this.isConnected) return;

      const items = [];
      products.forEach((product, index) => {
        if (product === null) store.remove('recent', handles[index]);
        if (!product || items.length >= limit) return;
        const item = cardFor(handles[index], product);
        if (item) items.push(item);
      });

      this.grid.replaceChildren(...items);
      this.hidden = items.length === 0;
      if (this.wrapper) this.wrapper.classList.toggle('etec-section--empty', items.length === 0);
    }
  }

  /* ---------- Wishlist page ----------------------------------------------------------- */

  class EtecWishlist extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-etec-grid]');
      this.empty = this.querySelector('[data-etec-empty]');
      this.tools = this.querySelector('[data-etec-tools]');
      this.summary = this.querySelector('[data-etec-summary]');
      this.loading = this.querySelector('[data-etec-loading]');
      this.clearButton = this.querySelector('[data-etec-clear]');
      if (!this.grid) return;

      this.onChange =
        this.onChange ||
        ((event) => {
          if (event.detail.list === 'wishlist') this.render();
        });
      this.onClear =
        this.onClear ||
        (() => {
          store.clear('wishlist');
          announce(TEXT.wishlistCleared);
        });

      document.addEventListener(CHANGE_EVENT, this.onChange);
      if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
    }

    async render() {
      const run = (this.run = (this.run || 0) + 1);
      const handles = store.read('wishlist');
      const products = handles.length ? await Promise.all(handles.map(fetchProduct)) : [];
      if (run !== this.run || !this.isConnected) return;

      // Products that no longer exist are dropped quietly; that write re-renders the list.
      const gone = handles.filter((handle, index) => products[index] === null);
      if (gone.length) {
        store.write(
          'wishlist',
          handles.filter((handle) => !gone.includes(handle))
        );
        return;
      }

      const existing = new Map();
      Array.from(this.grid.children).forEach((item) => existing.set(item.dataset.etecHandle, item));

      const items = [];
      handles.forEach((handle, index) => {
        if (!products[index]) return;
        const item = existing.get(handle) || cardFor(handle, products[index]);
        if (item) items.push(item);
      });

      // Only touch the DOM when the set of cards changed, so open quick add dialogs survive.
      const same = items.length === this.grid.children.length && items.every((item, i) => item === this.grid.children[i]);
      if (!same) this.grid.replaceChildren(...items);

      const count = items.length;
      if (this.loading) this.loading.hidden = true;
      this.grid.hidden = count === 0;
      if (this.empty) this.empty.hidden = count !== 0;
      if (this.tools) this.tools.hidden = count === 0;
      if (this.summary) this.summary.textContent = TEXT.saved(count);
    }
  }

  /* ---------- Compare page ------------------------------------------------------------- */

  class EtecCompare extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-etec-scroll]');
      this.empty = this.querySelector('[data-etec-empty]');
      this.tools = this.querySelector('[data-etec-tools]');
      this.loading = this.querySelector('[data-etec-loading]');
      this.highlight = this.querySelector('[data-etec-highlight]');
      this.clearButton = this.querySelector('[data-etec-clear]');
      if (!this.scroller) return;

      this.onChange =
        this.onChange ||
        ((event) => {
          if (event.detail.list === 'compare') this.render();
        });
      this.onClear =
        this.onClear ||
        (() => {
          store.clear('compare');
          announce(TEXT.compareCleared);
        });
      this.onHighlight = this.onHighlight || (() => this.syncHighlight());

      document.addEventListener(CHANGE_EVENT, this.onChange);
      if (this.clearButton) this.clearButton.addEventListener('click', this.onClear);
      if (this.highlight) this.highlight.addEventListener('change', this.onHighlight);
      compareBar.sync();
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      if (this.clearButton) this.clearButton.removeEventListener('click', this.onClear);
      if (this.highlight) this.highlight.removeEventListener('change', this.onHighlight);
    }

    syncHighlight() {
      const table = this.scroller.querySelector('table');
      if (table) table.classList.toggle('is-highlighting', Boolean(this.highlight && this.highlight.checked));
    }

    async render() {
      const run = (this.run = (this.run || 0) + 1);
      const handles = store.read('compare');
      const results = handles.length ? await Promise.all(handles.map(fetchProduct)) : [];
      if (run !== this.run || !this.isConnected) return;

      const gone = handles.filter((handle, index) => results[index] === null);
      if (gone.length) {
        store.write(
          'compare',
          handles.filter((handle) => !gone.includes(handle))
        );
        return;
      }

      const products = [];
      handles.forEach((handle, index) => {
        if (results[index]) products.push({ handle, ...results[index] });
      });

      if (this.loading) this.loading.hidden = true;
      this.scroller.hidden = products.length === 0;
      if (this.empty) this.empty.hidden = products.length !== 0;
      if (this.tools) this.tools.hidden = products.length === 0;
      if (this.highlight) {
        this.highlight.disabled = products.length < 2;
        if (products.length < 2) this.highlight.checked = false;
      }

      this.scroller.replaceChildren(...(products.length ? [this.buildTable(products)] : []));
      this.syncHighlight();
    }

    buildTable(products) {
      const table = el('table', 'etec-compare__table');
      table.style.setProperty('--etec-compare-columns', products.length);
      table.append(el('caption', 'visually-hidden', TEXT.compareRegion));

      /* Header: image, title, remove */
      const head = el('thead');
      const headRow = el('tr');
      const corner = el('td', 'etec-compare__label etec-compare__corner');
      corner.append(el('span', 'etec-compare__corner-text', TEXT.compareCount(products.length)));
      headRow.append(corner);

      products.forEach((product) => {
        const { data, handle } = product;
        const cell = el('th', 'etec-compare__product');
        cell.scope = 'col';

        const remove = el('button', 'etec-compare__remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', TEXT.remove(data.title));
        /* The icon is rendered by Liquid into a <template>; the text glyph is the fallback. */
        const icon = this.querySelector('template[data-etec-icon="close"]');
        remove.append(icon ? icon.content.cloneNode(true) : document.createTextNode('×'));
        remove.addEventListener('click', () => {
          store.remove('compare', handle);
          announce(TEXT.compareRemoved(data.title));
          this.scroller.focus({ preventScroll: true });
        });

        const media = el('a', 'etec-compare__media');
        media.href = data.url;
        media.tabIndex = -1;
        media.setAttribute('aria-hidden', 'true');
        if (data.image) {
          const image = el('img');
          image.alt = '';
          image.width = 180;
          image.height = 180;
          image.src = data.image;
          media.append(image);
        } else {
          media.append(el('span', 'etec-noimg'));
        }

        const title = el('a', 'etec-compare__title', data.title);
        title.href = data.url;

        cell.append(remove, media, title);
        headRow.append(cell);
      });
      head.append(headRow);
      table.append(head);

      /* Body: price, stock, buy, then the specifications */
      const body = el('tbody');

      const addRow = (label, fill, className) => {
        const row = el('tr', className || '');
        const heading = el('th', 'etec-compare__label', label);
        heading.scope = 'row';
        row.append(heading);
        products.forEach((product) => {
          const cell = el('td', 'etec-compare__cell');
          fill(cell, product);
          row.append(cell);
        });
        body.append(row);
        return row;
      };

      // Rows whose values are not the same for every product get marked for "Highlight differences".
      const markDifferent = (row, values) => {
        const normalised = values.map((value) => (value == null ? '' : String(value).trim().toLowerCase()));
        if (products.length < 2 || new Set(normalised).size < 2) return;
        row.classList.add('is-different');
        row.dataset.etecDifferent = '';
      };

      const priceRow = addRow(TEXT.price, (cell, { data }) => {
        const price = el('span', 'etec-compare__price', data.price_varies ? `${TEXT.from} ${data.price}` : data.price);
        cell.append(price);
        if (data.compare_at_price) cell.append(' ', el('s', 'etec-compare__was', data.compare_at_price));
      });
      markDifferent(
        priceRow,
        products.map(({ data }) => data.price)
      );

      const stockRow = addRow(TEXT.availability, (cell, { data }) => {
        const stock = el('span', `etec-stock${data.available ? '' : ' etec-stock--out'}`);
        const dot = el('span', 'etec-stock__dot');
        dot.setAttribute('aria-hidden', 'true');
        stock.append(dot, data.available ? TEXT.inStock : TEXT.outOfStock);
        cell.append(stock);
      });
      markDifferent(
        stockRow,
        products.map(({ data }) => data.available)
      );

      const buyRow = addRow(
        '',
        (cell, product) => {
          if (product.buy) cell.append(document.importNode(product.buy.content, true));
        },
        'etec-compare__buy'
      );
      const buyHeading = buyRow.querySelector('th');
      const buyLabel = el('td', 'etec-compare__label');
      buyHeading.replaceWith(buyLabel);

      /* Specification rows: the union of labels across products, grouped by heading. */
      const groups = new Map();
      products.forEach(({ data }, column) => {
        (data.specs || []).forEach((spec) => {
          if (!spec || typeof spec.label !== 'string' || spec.value == null || typeof spec.value === 'object') return;
          const groupName = spec.group || '';
          const groupKey = groupName.toLowerCase();
          if (!groups.has(groupKey)) groups.set(groupKey, { name: groupName, rows: new Map() });
          const rows = groups.get(groupKey).rows;
          const rowKey = spec.label.toLowerCase();
          if (!rows.has(rowKey)) rows.set(rowKey, { label: spec.label, values: new Array(products.length).fill(null) });
          const values = rows.get(rowKey).values;
          const value = String(spec.value);
          values[column] = values[column] ? `${values[column]}, ${value}` : value;
        });
      });

      groups.forEach((group) => {
        const groupRow = el('tr', 'etec-compare__group');
        const groupCell = el('th', 'etec-compare__label', group.name || TEXT.general);
        groupCell.scope = 'rowgroup';
        const filler = el('td', 'etec-compare__group-fill');
        filler.colSpan = products.length;
        groupRow.append(groupCell, filler);
        body.append(groupRow);

        group.rows.forEach((spec) => {
          const row = addRow(
            spec.label,
            (cell, product) => {
              const value = spec.values[products.indexOf(product)];
              if (value == null) {
                const dash = el('span', 'etec-compare__missing', TEXT.missing);
                dash.setAttribute('aria-hidden', 'true');
                cell.append(dash, el('span', 'visually-hidden', TEXT.notProvided));
              } else {
                cell.textContent = value;
              }
            }
          );
          markDifferent(row, spec.values);
        });
      });

      table.append(body);
      return table;
    }
  }

  /* ---------- Start -------------------------------------------------------------------- */

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  // For other scripts and for checking things from the console.
  window.EtecLists = {
    read: store.read,
    add: store.add,
    remove: store.remove,
    clear: store.clear,
    has: store.has,
    limits: LIMITS,
    event: CHANGE_EVENT,
  };

  define('etec-list-toggle', EtecListToggle);
  define('etec-list-count', EtecListCount);
  define('etec-recent-tracker', EtecRecentTracker);
  define('etec-recently-viewed', EtecRecentlyViewed);
  define('etec-wishlist', EtecWishlist);
  define('etec-compare', EtecCompare);

  compareBar.sync();
})();
